import React from "react";
import {
  dataUrl,
  getJobLocationById,
  getWorkExpText,
  toArray,
} from "../../../../helpers/common";
import {
  getEndData,
  getStartData,
} from "../../../Account/Candidate/MyProfile/ExperienceJourney";
import WebsiteLogo from "../../../../helpers/WebsiteLogo";
import styles from "./ResumeTemplateModern.module.css";
import { getCertificationValidity } from "..";

/**
 * Helper function to safely extract start and end dates/years from education objects
 */
const formatEducationDate = (fields) => {
  if (!fields) return "";

  // Extract start date / year
  const start =
    fields.start_year ||
    fields.startYear ||
    fields.start_date ||
    fields.fromDate ||
    fields.from_year;

  // Extract end date / passing year
  const end =
    fields.end_year ||
    fields.endYear ||
    fields.end_date ||
    fields.toDate ||
    fields.to_year ||
    fields.passout_year ||
    fields.passing_year ||
    fields.year_of_passing ||
    fields.completion_year;

  // Check pursuing status
  const isPursuing =
    fields.currently_studying ||
    fields.is_current ||
    fields.pursuing ||
    fields.currently_pursuing;

  if (start && end) return `${start} - ${end}`;
  if (start && isPursuing) return `${start} - Present`;
  if (start) return `${start}`;
  if (end) return `${end}`;

  // Helper fallback check
  const startFromHelper = getStartData(fields);
  const endFromHelper = getEndData(fields);

  if (
    startFromHelper &&
    endFromHelper &&
    startFromHelper !== "undefined" &&
    endFromHelper !== "undefined"
  ) {
    return `${startFromHelper} - ${endFromHelper}`;
  }

  return "";
};

const Sidebar = ({ formData, jobLocations, experiences }) => {
  // Extract position fallback from most recent work experience if direct designation is missing
  let jobTitle =
    formData?.designation ||
    formData?.job_title ||
    formData?.title ||
    formData?.current_designation;

  if (!jobTitle && experiences && experiences.length > 0) {
    const latestJob = experiences[0];
    const fields = latestJob?.additional_fields || latestJob;
    jobTitle = fields?.position || fields?.designation || fields?.job_title;
  }

  const locationName = formData?.location
    ? getJobLocationById(formData.location, jobLocations)
    : "";

  // Safe parsing for skills array
  const rawSkills = Array.isArray(formData?.skills)
    ? formData.skills
    : formData?.skills?.skills || formData?.skills;

  let parsedSkills = [];
  if (typeof rawSkills === "string") {
    parsedSkills = toArray(rawSkills, ",");
  } else if (Array.isArray(rawSkills)) {
    parsedSkills = rawSkills;
  } else {
    parsedSkills = toArray(rawSkills);
  }

  const skillsArr = parsedSkills
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (typeof item === "object" && item !== null) {
        return (
          item?.skill_name ||
          item?.name ||
          item?.skill ||
          item?.title ||
          ""
        ).trim();
      }
      return "";
    })
    .filter(Boolean)
    .slice(0, 15);

  const hobbiesArr = toArray(formData?.hobbies)?.slice(0, 15);
  const languagesArr = toArray(formData?.languages_known)?.slice(0, 15);

  return (
    <div className={styles.sidebar}>
      {formData?.profile_picture && (
        <img
          src={`${formData.profile_picture}`}
          alt={formData?.name || "Profile"}
          className={styles.profilePic}
        />
      )}
      {formData?.name && <h1 className={styles.name}>{formData.name}</h1>}
      {jobTitle && <p className={styles.designation}>{jobTitle}</p>}

      <div className={styles.contactInfo}>
        <h2 className={styles.sectionTitle}>Contacts</h2>

        {formData?.mobile_number && (
          <p className={styles.contactItem}>
            <strong>Phone</strong>
            <span> {formData.mobile_number}</span>
          </p>
        )}
        {formData?.email && (
          <p className={styles.contactItem}>
            <strong>Email</strong>
            <span>
              <a href={`mailto:${formData.email}`}>{formData.email}</a>
            </span>
          </p>
        )}
        {locationName && (
          <p className={styles.contactItem}>
            <strong>Location</strong>
            <span>{locationName}, India</span>
          </p>
        )}
      </div>

      {skillsArr.length > 0 && (
        <div className={styles.skills}>
          <h2 className={styles.sectionTitle}>Skills</h2>
          <ul>
            {skillsArr.map((skill, index) => (
              <li key={index}>
                <strong>-</strong> {skill}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hobbiesArr.length > 0 && (
        <div className={styles.skills}>
          <h2 className={styles.sectionTitle}>Hobbies</h2>
          <ul>
            {hobbiesArr.map((item, index) => (
              <li key={index}>
                <strong>-</strong> {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {languagesArr.length > 0 && (
        <div className={styles.skills}>
          <h2 className={styles.sectionTitle}>Languages</h2>
          <ul>
            {languagesArr.map((item, index) => (
              <li key={index}>
                <strong>-</strong> {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Experience = ({ experience, resumeBuilder }) => {
  if (!experience?.length) return null;

  const rawExpText = resumeBuilder?.total_work_experience
    ? getWorkExpText(resumeBuilder?.total_work_experience)?.trim()
    : "";

  const totalExperience =
    rawExpText && rawExpText !== "years" && rawExpText !== "year"
      ? `(${rawExpText})`
      : "";

  return (
    <section>
      <h2 className={styles.sectionTitle}>
        Work Experience {totalExperience}
      </h2>
      {experience.map((job, index) => {
        const jobData = job?.additional_fields || job;

        const startDate = getStartData(jobData);
        const endDate = getEndData(jobData);

        let dateDisplay = "";
        if (startDate && endDate) {
          dateDisplay =
            endDate === "Present"
              ? `${startDate} - Present`
              : `${startDate} - ${endDate}`;
        } else if (startDate) {
          dateDisplay = startDate;
        } else if (endDate) {
          dateDisplay = endDate;
        }

        const position =
          jobData?.position || jobData?.designation || jobData?.job_title;
        const company =
          jobData?.company_name || jobData?.company || jobData?.organization;
        const details = jobData?.details;

        return (
          <div className={styles.job} key={index}>
            {position && <p className={styles.jobTitle}>{position}</p>}

            {(company || dateDisplay) && (
              <p className={styles.jobDate}>
                {company}
                {company && dateDisplay ? " - " : ""}
                {dateDisplay ? `(${dateDisplay})` : ""}
              </p>
            )}

            {details && (
              <div className={styles.jobDetails}>
                {toArray(details, "\n").map((detail, i) => (
                  <p key={i}>{detail}</p>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

const Education = ({ educations }) => {
  if (!educations?.length) return null;

  return (
    <section>
      <h2 className={styles.sectionTitle}>Education</h2>

      {educations.map((education, idx) => {
        const fields = education?.additional_fields || education;

        const degreeTitle =
          fields?.title ||
          fields?.degree ||
          fields?.qualification ||
          fields?.course;

        const schoolName =
          fields?.university_college ||
          fields?.university ||
          fields?.college ||
          fields?.school ||
          fields?.institution;

        const dateRange = formatEducationDate(fields);
        const certification = fields?.certification;

        return (
          <div key={idx} className="mb-3">
            {degreeTitle && (
              <p className="mb-0">
                <strong>{degreeTitle}</strong>
              </p>
            )}

            {(schoolName || dateRange) && (
              <p className={styles.jobDate}>
                {schoolName}
                {schoolName && dateRange ? " - " : ""}
                {dateRange ? `(${dateRange})` : ""}
              </p>
            )}

            {certification && (
              <p style={{ color: "#0008" }}>
                <strong>Certification</strong>: {certification}
              </p>
            )}
          </div>
        );
      })}
    </section>
  );
};

const Certification = ({ certifications }) => {
  if (!certifications?.length) return null;

  return (
    <section>
      <h2 className={styles.sectionTitle}>Certification</h2>

      {certifications.map((cert, idx) => {
        const fields = cert?.additional_fields || cert;

        const title =
          fields?.title || fields?.name || fields?.certificate_name;
        const organization = fields?.organization || fields?.issuer;
        const url = fields?.url || fields?.link;
        const validityText = getCertificationValidity(cert);

        return (
          <div key={idx} className="mb-3">
            {title && (
              <p className="mb-0">
                <strong>{title}</strong>
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ms-2"
                  >
                    <i className="uil uil-external-link-alt"></i>
                  </a>
                )}
              </p>
            )}

            {organization && (
              <span className={`${styles.jobDate} me-2`}>
                <strong>{organization}</strong>
              </span>
            )}

            {validityText && (
              <p className={styles.jobDate}>{validityText}</p>
            )}
          </div>
        );
      })}
    </section>
  );
};

const ResumeTemplateModern = ({
  formData,
  jobLocations,
  experiences,
  certifications,
  qualifications,
}) => {
  return (
    <div className={styles.resumeContainer}>
      <Sidebar
        formData={formData}
        jobLocations={jobLocations}
        experiences={experiences}
      />
      <div className={styles.mainContent}>
        {formData?.details && (
          <section>
            <h2 className={styles.sectionTitle}>Summary</h2>
            <p>{formData.details}</p>
          </section>
        )}
        <Experience experience={experiences} resumeBuilder={formData} />
        <Education educations={qualifications} />
        <Certification certifications={certifications} />

        {/* Resume Footer */}
        <div className="resume-footer">
          Generated by{" "}
          <a href="https://webvi.in/">
            <WebsiteLogo />
          </a>
        </div>
      </div>
    </div>
  );
};

export default ResumeTemplateModern;